import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { AppLayoutContext } from '../../../providers/AppLayout'
import { useAdminAccess } from '../../../providers/AdminAccessContext'
import { useUrlStateParams } from '../../../utils/url'
import { AppConfig } from '../../App.config'
import { SnackbarContext } from '../../../providers/SnackbarContext'

import { Hospital } from '../../../models/Hospital.model'
import { Review } from '../../../models/Review.model'
import { HospitalUpdateEvent } from '../../../events/HospitalUpdateEvent'

import { HospitalService } from '../../../services/HospitalService/HospitalService'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Link,
  Paper,
  Rating,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { InputAdornment } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import PhoneIcon from '@mui/icons-material/Phone'
import EmailIcon from '@mui/icons-material/Email'
import LanguageIcon from '@mui/icons-material/Language'
import InfoIcon from '@mui/icons-material/Info'
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera'
import Loading from '../../../components/Loading/Loading.component'
import Breadcrumbs from '../../../components/Breadcrumbs/Breadcrumbs.component'
import HospitalFormDialog from '../shared/HospitalFormDialog.component'
import HospitalIcon from '../../../icons/HospitalIcon'

const HospitalDetail = (): JSX.Element => {
  const { isAdmin } = useAdminAccess()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [isLoading, setIsLoading] = useState(true)
  const [loadingError, setLoadingError] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [savingError, setSavingError] = useState('')
  const [isEditing, setIsEditing] = useUrlStateParams<boolean>(
    false,
    'edit',
    (value) => (value ? 't' : ''),
    (value) => value === 't',
  )
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false)
  const [isUploadingLogo, setIsUploadingLogo] = useState(false)
  const [hospital, setHospital] = useState<Hospital>()
  const [openReviews, setOpenReviews] = useState<Review[]>([])
  const [reviewSearch, setReviewSearch] = useState('')
  const [reviewsToShow, setReviewsToShow] = useState(10)

  const cardSx = {
    borderRadius: 3,
    border: '1px solid rgba(0, 0, 0, 0.06)',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.1)',
    backgroundColor: '#FFFFFF',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  }

  const hospitalService = useMemo(() => new HospitalService(), [])

  useEffect(() => {
    AppLayoutContext.setPageName(`Hospital Detail`)
  }, [hospital, id])

  useEffect(() => {
    setIsLoading(true)
    setLoadingError('')
    hospitalService
      .searchOne({ id: Number(id) })
      .then((result) => {
        setIsLoading(false)
        setHospital(result)
        setOpenReviews(result.reviews || [])
      })
      .catch((err) => {
        setLoadingError(err?.data?.message || 'Failed to load hospital')
        console.error(err)
        setIsLoading(false)
      })
  }, [id])

  const canShowUpload = isAdmin

  const renderReviewCard = (review: Review) => (
    <Card
      key={review.id}
      sx={{
        ...cardSx,
        borderRadius: 2,
        border: '1px solid rgba(0, 0, 0, 0.08)',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        },
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 1.5 }}>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontSize: '0.75rem',
                display: 'block',
                mb: 0.5,
              }}
            >
              {new Date(review.createdAt).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              })}
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                color: '#263238',
                fontSize: '1rem',
                lineHeight: 1.4,
              }}
            >
              {review.position || review.title || 'Review'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
            <Rating
              value={Number(review.averageRating) || 0}
              precision={0.5}
              readOnly
              size="small"
              sx={{
                '& .MuiRating-iconFilled': {
                  color: '#FF9800',
                },
                '& .MuiRating-iconEmpty': {
                  color: '#E0E0E0',
                },
              }}
            />
            {review.averageRating && (
              <Typography
                variant="body2"
                sx={{
                  color: '#263238',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  minWidth: '24px',
                }}
              >
                {review.averageRating}
              </Typography>
            )}
          </Box>
        </Box>
        {review.additionalFeedback && (
          <Typography
            variant="body2"
            sx={{
              color: '#263238',
              fontSize: '0.875rem',
              lineHeight: 1.6,
              mt: 1,
            }}
          >
            {review.additionalFeedback}
          </Typography>
        )}
      </CardContent>
    </Card>
  )

  const filteredReviews = openReviews.filter((review) => {
    const query = reviewSearch.trim().toLowerCase()
    if (!query) return true
    return (
      String(review.id).includes(query) ||
      (review.title || '').toLowerCase().includes(query) ||
      (review.position || '').toLowerCase().includes(query) ||
      (review.additionalFeedback || '').toLowerCase().includes(query)
    )
  })

  // Reset reviews to show when search changes
  useEffect(() => {
    setReviewsToShow(10)
  }, [reviewSearch])

  const displayedReviews = filteredReviews.slice(0, reviewsToShow)
  const hasMoreReviews = filteredReviews.length > 10 && filteredReviews.length > reviewsToShow

  const handleLoadMore = () => {
    setReviewsToShow((prev) => prev + 10)
  }

  const handleEdit = (
    changedProperties: HospitalUpdateEvent,
    setIsEditFormSubmitting: React.Dispatch<React.SetStateAction<boolean>>,
  ) => {
    if (!Object.keys(changedProperties).length) {
      // nothing changed so just close the dialog
      setIsEditing(false)
    } else {
      setIsSaving(true)
      setSavingError('')
      hospitalService
        .update(Number(id), changedProperties)
        .then((result) => {
          SnackbarContext.show(`Hospital saved successfully`)
          setIsSaving(false)
          setHospital(result)
          setIsEditing(false)
        })
        .catch((err) => {
          SnackbarContext.show(`Hospital failed to edit: ${err?.data?.message || err?.message || err}`, 'error')
          setSavingError(err)
          console.error(err)
          setIsSaving(false)
          setIsEditFormSubmitting(false)
        })
    }
  }

  const handleDeleteClick = () => {
    // show a confirmation dialog before deleting
    setIsDeleteConfirmationOpen(true)
  }

  const handleDelete = () => {
    if (hospital?.id) {
      setIsLoading(true)
      setIsDeleteConfirmationOpen(false)
      hospitalService
        .delete(hospital.id)
        .then(() => {
          SnackbarContext.show(`Hospital deleted successfully. You will now be redirected.`)

          // after a delay of 1 second, redirect to the hospital list page
          setTimeout(() => {
            navigate('/hospitals')
          }, 1000)
        })
        .catch((err) => {
          console.error(err)
          SnackbarContext.show(`Hospital failed to delete: ${err?.data?.message || err?.message || err}`, 'error')
          setIsLoading(false)
        })
    }
  }

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!canShowUpload) {
      event.target.value = ''
      SnackbarContext.show('You do not have permission to upload a hospital logo.', 'error')
      return
    }
    const file = event.target.files?.[0]
    if (!file || !hospital?.id) return

    const allowedMimeTypes = ['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'image/svg+xml']
    if (!allowedMimeTypes.includes(file.type)) {
      SnackbarContext.show('Invalid file type. Please upload PNG, JPEG, WEBP, GIF, or SVG.', 'error')
      return
    }

    const maxFileSizeBytes = 5 * 1024 * 1024
    if (file.size > maxFileSizeBytes) {
      SnackbarContext.show('File is too large. Maximum allowed size is 5MB.', 'error')
      return
    }

    setIsUploadingLogo(true)
    hospitalService
      .uploadLogo(hospital.id, file)
      .then((updatedHospital) => {
        setHospital(updatedHospital)
        SnackbarContext.show('Hospital logo updated successfully')
      })
      .catch((err) => {
        const statusCode = err?.status
        const backendMessage = err?.data?.message
        let errorMessage = backendMessage || 'Hospital logo upload failed.'

        if (statusCode === 400) {
          errorMessage = backendMessage || 'Invalid logo file. Please check file type and size.'
        } else if (statusCode === 401 || statusCode === 403) {
          errorMessage = 'You do not have permission to upload a hospital logo.'
        } else if (statusCode === 404) {
          errorMessage = 'Hospital not found. Please refresh the page and try again.'
        } else if (statusCode === 500) {
          errorMessage = 'Server error while uploading logo. Please try again.'
        }

        SnackbarContext.show(errorMessage, 'error')
      })
      .finally(() => {
        setIsUploadingLogo(false)
      })

    event.target.value = ''
  }



  return (
    <>
      {hospital && (
        <HospitalFormDialog
          open={isEditing}
          hospital={hospital}
          onEdit={handleEdit}
          onCancel={() => setIsEditing(false)}
          onClose={() => setIsEditing(false)}
        />
      )}
      <Dialog
        fullWidth
        maxWidth="xs"
        open={isDeleteConfirmationOpen}
        onClose={() => setIsDeleteConfirmationOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent dividers>Are you sure you want to delete this hospital?</DialogContent>
        <DialogActions>
          <Button autoFocus onClick={() => setIsDeleteConfirmationOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleDelete}>Confirm</Button>
        </DialogActions>
      </Dialog>
      <Paper sx={{ p: 3 }}>
        {isLoading && !loadingError && <Loading sx={{ py: 20 }} />}
        {loadingError && (
          <Stack direction="row" justifyContent="center" alignItems="center" sx={{ py: 20 }}>
            <Typography variant="error">{loadingError}</Typography>
          </Stack>
        )}
        {!isLoading && !loadingError && hospital && (
          <>
            {isSaving && !savingError && <Loading sx={{ py: 20 }} />}
            {savingError && (
              <Stack direction="row" justifyContent="center" alignItems="center" sx={{ py: 20 }}>
                <Typography variant="error">{savingError}</Typography>
              </Stack>
            )}
            {!isSaving && !savingError && (
              <>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                  <Breadcrumbs items={[{ label: 'Hospitals', href: '/hospitals' }, { label: hospital.name }]} />
                  <Stack direction="row" justifyContent="end" alignItems="center" spacing={2}>
                    {isAdmin && (
                      <Button variant="outlined" color="error" onClick={handleDeleteClick}>
                        Delete
                      </Button>
                    )}
                    {isAdmin && (
                      <Button variant="contained" onClick={() => setIsEditing(true)}>
                        Edit
                      </Button>
                    )}
                  </Stack>
                </Stack>
                <Stack spacing={3}>
                  <Card
                    sx={{
                      ...cardSx,
                      borderRadius: 3,
                      background: 'linear-gradient(135deg, rgba(0, 124, 145, 0.06) 0%, rgba(0, 172, 193, 0.04) 100%)',
                      border: '1px solid rgba(0, 124, 145, 0.12)',
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        right: -30,
                        top: -30,
                        opacity: 0.08,
                        zIndex: 0,
                        transform: 'rotate(-15deg)',
                      }}
                    >
                      <HospitalIcon sx={{ fontSize: 280, color: 'primary.main' }} />
                    </Box>
                    <CardContent sx={{ p: { xs: 2, sm: 3 }, position: 'relative', zIndex: 1 }}>
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={{ xs: 2, sm: 3 }}
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                      >
                        <Box
                          sx={{
                            width: { xs: '100%', sm: 200 },
                            height: { xs: 200, sm: 200 },
                            minWidth: { xs: '100%', sm: 200 },
                            minHeight: { xs: 200, sm: 200 },
                            bgcolor: '#FFFFFF',
                            border: '2px solid rgba(0, 124, 145, 0.2)',
                            borderRadius: 3,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            position: 'relative',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                            flexShrink: 0,
                            cursor: canShowUpload && !isUploadingLogo ? 'pointer' : 'default',
                          }}
                          component={canShowUpload ? ('label' as any) : ('div' as any)}
                        >
                          <Box
                            component="img"
                            src={
                              hospital.imageURL ||
                              'https://scrubstr-wp-uploads.s3.us-east-2.amazonaws.com/2021/01/07212249/hospital-placeholder.png'
                            }
                            alt={hospital.name}
                            sx={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain',
                              objectPosition: 'center',
                              px: 1,
                            }}
                          />
                          {canShowUpload && (
                            <>
                              <input hidden accept="image/*" type="file" onChange={handleLogoUpload} disabled={isUploadingLogo} />
                              <Box
                                sx={{
                                  position: 'absolute',
                                  right: 10,
                                  bottom: 10,
                                  width: 36,
                                  height: 36,
                                  borderRadius: '50%',
                                  backgroundColor: 'rgba(0, 124, 145, 0.92)',
                                  color: '#fff',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.28)',
                                  transition: 'all 0.2s ease',
                                  opacity: isUploadingLogo ? 0.75 : 1,
                                  '&:hover': {
                                    backgroundColor: 'rgba(0, 124, 145, 1)',
                                    transform: isUploadingLogo ? 'none' : 'scale(1.05)',
                                  },
                                }}
                              >
                                <PhotoCameraIcon sx={{ fontSize: 20 }} />
                              </Box>
                            </>
                          )}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography sx={{ fontWeight: 700, color: '#263238', mb: 0.5, fontSize: { xs: '1.25rem', sm: '1.75rem', md: '2.125rem' } }}>
                            {hospital.name}
                          </Typography>
                          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
                            {hospital.averageRating && (
                              <>
                                <Rating
                                  value={Number(hospital.averageRating) || 0}
                                  precision={0.5}
                                  readOnly
                                  size="small"
                                  sx={{
                                    '& .MuiRating-iconFilled': {
                                      color: '#FF9800',
                                    },
                                    '& .MuiRating-iconEmpty': {
                                      color: '#E0E0E0',
                                    },
                                  }}
                                />
                                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                  {hospital.averageRating} / 5
                                </Typography>
                              </>
                            )}
                          </Stack>
                          <Box
                            sx={{
                              p: 1.5,
                              bgcolor: 'rgba(255, 255, 255, 0.6)',
                              borderRadius: 2,
                              border: '1px solid rgba(0, 124, 145, 0.1)',
                              display: 'inline-block',
                              width: 'fit-content',
                              maxWidth: '100%',
                            }}
                          >
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                              App Url
                            </Typography>
                            <Link
                              variant="body2"
                              href={`${AppConfig.companionAppUrl}/hospitals/${hospital.permalink}`}
                              target="_blank"
                              sx={{
                                color: 'primary.main',
                                fontWeight: 500,
                                textDecoration: 'none',
                                wordBreak: 'break-all',
                                overflowWrap: 'break-word',
                                display: 'block',
                                '&:hover': {
                                  textDecoration: 'underline',
                                },
                              }}
                            >
                              {AppConfig.companionAppUrl}/hospitals/{hospital.permalink}
                            </Link>
                          </Box>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
                      gap: 3,
                      alignItems: 'stretch',
                    }}
                  >
                    <Card sx={{ ...cardSx, height: '100%' }}>
                      <CardContent sx={{ p: 3 }}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                          <LocationOnIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                          <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                            Contact & Location
                          </Typography>
                        </Stack>
                        <Grid container spacing={3}>
                          <Grid item xs={12} md={6}>
                            <Typography
                              variant="overline"
                              sx={{
                                color: 'primary.main',
                                fontWeight: 600,
                                fontSize: '0.7rem',
                                letterSpacing: 1.2,
                                display: 'block',
                                mb: 1.5,
                              }}
                            >
                              Address
                            </Typography>
                            {hospital.address && (
                              <Typography variant="body2" sx={{ color: '#263238', mb: 0.5, fontWeight: 500 }}>
                                {hospital.address}
                              </Typography>
                            )}
                            {(hospital.city || hospital.state || hospital.zipcode) && (
                              <Typography variant="body2" sx={{ color: '#263238', mb: 1 }}>
                                {[hospital.city, hospital.state, hospital.zipcode].filter(Boolean).join(', ')}
                              </Typography>
                            )}
                            {hospital.latitude !== null && hospital.longitude !== null && (
                              <Box
                                sx={{
                                  mt: 1,
                                  bgcolor: 'rgba(0, 0, 0, 0.04)',
                                  p: 0.75,
                                  borderRadius: 1,
                                  display: 'inline-block',
                                  width: 'fit-content',
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: 'text.secondary',
                                    display: 'block',
                                    fontFamily: 'monospace',
                                    mb: 0.5,
                                  }}
                                >
                                  {[hospital.latitude, hospital.longitude].join(', ')}
                                </Typography>
                                <Link
                                  href={`https://www.google.com/maps?q=${hospital.latitude},${hospital.longitude}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{
                                    color: 'primary.main',
                                    fontSize: '0.75rem',
                                    textDecoration: 'none',
                                    fontWeight: 500,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    '&:hover': {
                                      textDecoration: 'underline',
                                    },
                                  }}
                                >
                                  <LocationOnIcon sx={{ fontSize: 16 }} />
                                  Open in Google Maps
                                </Link>
                              </Box>
                            )}
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Typography
                              variant="overline"
                              sx={{
                                color: 'primary.main',
                                fontWeight: 600,
                                fontSize: '0.7rem',
                                letterSpacing: 1.2,
                                display: 'block',
                                mb: 1.5,
                              }}
                            >
                              Support
                            </Typography>
                            <Stack spacing={1.5}>
                              {hospital.website &&
                                hospital.website.toUpperCase() !== 'NOT AVAILABLE' &&
                                hospital.website.trim() !== '' && (
                                  <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ flexWrap: 'wrap' }}>
                                    <LanguageIcon
                                      sx={{ color: 'text.secondary', fontSize: 18, mt: 0.5, flexShrink: 0 }}
                                    />
                                    <Link
                                      href={hospital.website}
                                      target="_blank"
                                      sx={{
                                        color: '#263238',
                                        fontWeight: 500,
                                        textDecoration: 'none',
                                        wordBreak: 'break-all',
                                        overflowWrap: 'break-word',
                                        flex: 1,
                                        minWidth: 0,
                                        '&:hover': {
                                          color: 'primary.main',
                                          textDecoration: 'underline',
                                        },
                                      }}
                                    >
                                      {hospital.website}
                                    </Link>
                                  </Stack>
                                )}
                              {hospital.supportTelephone &&
                                hospital.supportTelephone.toUpperCase() !== 'NOT AVAILABLE' &&
                                hospital.supportTelephone.trim() !== '' && (
                                  <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ flexWrap: 'wrap' }}>
                                    <PhoneIcon sx={{ color: 'text.secondary', fontSize: 18, mt: 0.5, flexShrink: 0 }} />
                                    <Link
                                      href={`tel:${hospital.supportTelephone}`}
                                      sx={{
                                        color: '#263238',
                                        fontWeight: 500,
                                        textDecoration: 'none',
                                        wordBreak: 'break-all',
                                        overflowWrap: 'break-word',
                                        flex: 1,
                                        minWidth: 0,
                                        '&:hover': {
                                          color: 'primary.main',
                                          textDecoration: 'underline',
                                        },
                                      }}
                                    >
                                      {hospital.supportTelephone}
                                    </Link>
                                  </Stack>
                                )}
                              {hospital.supportEmail &&
                                hospital.supportEmail.toUpperCase() !== 'NOT AVAILABLE' &&
                                hospital.supportEmail.trim() !== '' && (
                                  <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ flexWrap: 'wrap' }}>
                                    <EmailIcon sx={{ color: 'text.secondary', fontSize: 18, mt: 0.5, flexShrink: 0 }} />
                                    <Link
                                      href={`mailto:${hospital.supportEmail}`}
                                      sx={{
                                        color: '#263238',
                                        fontWeight: 500,
                                        textDecoration: 'none',
                                        wordBreak: 'break-all',
                                        overflowWrap: 'break-word',
                                        flex: 1,
                                        minWidth: 0,
                                        '&:hover': {
                                          color: 'primary.main',
                                          textDecoration: 'underline',
                                        },
                                      }}
                                    >
                                      {hospital.supportEmail}
                                    </Link>
                                  </Stack>
                                )}
                              {(!hospital.website ||
                                hospital.website.toUpperCase() === 'NOT AVAILABLE' ||
                                hospital.website.trim() === '') &&
                                (!hospital.supportTelephone ||
                                  hospital.supportTelephone.toUpperCase() === 'NOT AVAILABLE' ||
                                  hospital.supportTelephone.trim() === '') &&
                                (!hospital.supportEmail ||
                                  hospital.supportEmail.toUpperCase() === 'NOT AVAILABLE' ||
                                  hospital.supportEmail.trim() === '') && (
                                  <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                                    No support details available.
                                  </Typography>
                                )}
                            </Stack>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                    <Card sx={{ ...cardSx, height: '100%' }}>
                      <CardContent sx={{ p: 3 }}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                          <InfoIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                          <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                            System Details
                          </Typography>
                        </Stack>
                        <Stack spacing={2}>
                          <Box>
                            <Typography
                              variant="overline"
                              sx={{
                                color: 'primary.main',
                                fontWeight: 600,
                                fontSize: '0.7rem',
                                letterSpacing: 1.2,
                                display: 'block',
                                mb: 1.5,
                              }}
                            >
                              Facility Code
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#263238', fontWeight: 500 }}>
                              {hospital.facilityCode || '—'}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography
                              variant="overline"
                              sx={{
                                color: 'primary.main',
                                fontWeight: 600,
                                fontSize: '0.7rem',
                                letterSpacing: 1.2,
                                display: 'block',
                                mb: 1.5,
                              }}
                            >
                              Permalink
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ color: '#263238', fontWeight: 500, fontFamily: 'monospace' }}
                            >
                              {hospital.permalink || '—'}
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Box>

                  <Card sx={cardSx}>
                    <CardContent sx={{ p: 3 }}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ mb: 3, flexWrap: { xs: 'wrap', sm: 'nowrap' }, gap: 2 }}
                      >
                        <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
                          <Typography variant="h6" component="h3" sx={{ fontWeight: 600, color: 'primary.main' }}>
                            Reviews
                          </Typography>
                          <Chip
                            label={`${openReviews.length}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ fontWeight: 600, height: 24 }}
                          />
                        </Stack>
                        <TextField
                          size="small"
                          placeholder="Search reviews..."
                          value={reviewSearch}
                          onChange={(event) => setReviewSearch(event.target.value)}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            width: { xs: '100%', sm: 280 },
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                            },
                          }}
                        />
                      </Stack>
                      {filteredReviews.length === 0 ? (
                        <Box
                          sx={{
                            py: 6,
                            textAlign: 'center',
                            bgcolor: 'rgba(0, 0, 0, 0.02)',
                            borderRadius: 2,
                            border: '1px dashed rgba(0, 0, 0, 0.1)',
                          }}
                        >
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {reviewSearch ? 'No reviews match your search.' : 'No reviews available.'}
                          </Typography>
                        </Box>
                      ) : (
                        <>
                          <Stack spacing={2}>
                            {displayedReviews.map((review) => (
                              <Box key={review.id}>{renderReviewCard(review)}</Box>
                            ))}
                          </Stack>
                          {hasMoreReviews && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                              <Button
                                variant="outlined"
                                onClick={handleLoadMore}
                                sx={{
                                  borderRadius: 2,
                                  px: 4,
                                  py: 1,
                                  textTransform: 'none',
                                  fontWeight: 600,
                                }}
                              >
                                Load More ({filteredReviews.length - reviewsToShow} remaining)
                              </Button>
                            </Box>
                          )}
                        </>
                      )}
                    </CardContent>
                  </Card>
                </Stack>
              </>
            )}
          </>
        )}
      </Paper>
    </>
  )
}
export default HospitalDetail
